// @/components/announcements/AnnouncementDialog.jsx

/**
 * AnnouncementDialog
 *
 * Renderer for one announcement. Modes (decided by content):
 *   - text only         (no media uploaded)
 *   - text + video      (videoMedia uploaded)
 *   - text + image      (imageMedia uploaded)
 *
 * Backend lifecycle hook enforces video/image mutual exclusion in CMS, so
 * the renderer doesn't have to reconcile both being set. The /active
 * endpoint flattens the uploaded media's CDN URL into the response as
 * videoUrl / imageUrl strings — this component only knows about strings.
 *
 * Layout follows the canonical Catalyst Alert pattern (UserDeleteDialog,
 * BatchInviteDialog, ScmReminderDialog all share):
 *   - AlertTitle handles its own mb-4 sm:pr-12 — no manual margins
 *   - AlertDescription wraps body in <Text> with sm:pr-12 — no manual margins
 *   - AlertBody wraps secondary content blocks (media), each gets mt-4
 *   - AlertActions is right-aligned with mt-8 + gap-4
 *
 * Buttons are derived from CMS:
 *   - primaryCtaLabel set    → primary button (color=dark)
 *   - primaryCtaUrl set      → opens in new tab on click; if empty, just records cta_clicked
 *   - secondaryCtaLabel set  → secondary button → action='deferred'
 *   - dismissible=true       → X button + outside-click + ESC dismiss
 */

import React, { useEffect, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertBody,
  AlertActions,
} from '@/components/reusable/alert';
import { Button } from '@/components/reusable/button';
import Plyr from 'plyr-react';
import 'plyr-react/plyr.css';

const plyrOptions = {
  controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
  autoplay: false,
  muted: false,
  volume: 0.5,
  loop: { active: true },
  clickToPlay: true,
  keyboard: { focused: true, global: false },
  hideControls: false,
  resetOnEnd: false,
  iconUrl: '/plyr.svg',
  ratio: '1:1',
  storage: { enabled: false },
  speed: { selected: 1, options: [1] },
  displayDuration: true,
  invertTime: true,
  toggleInvert: true,
};

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Object} props.announcement   // { key, title, body, videoUrl, imageUrl, dismissible, primaryCtaLabel, primaryCtaUrl, secondaryCtaLabel }
 * @param {(action: 'dismissed'|'cta_clicked'|'deferred') => void} props.onAction
 */
const AnnouncementDialog = ({ isOpen, announcement, onAction }) => {
  const plyrRef = useRef(null);
  const hasVideo = !!announcement?.videoUrl;
  const hasImage = !hasVideo && !!announcement?.imageUrl;
  const dismissible = announcement?.dismissible !== false;
  const hasPrimary = !!announcement?.primaryCtaLabel;
  const hasSecondary = !!announcement?.secondaryCtaLabel;

  useEffect(() => {
    if (isOpen && hasVideo && plyrRef.current?.plyr) {
      plyrRef.current.plyr.volume = 0.5;
    }
  }, [isOpen, hasVideo]);

  if (!isOpen || !announcement) return null;

  const handleDismiss = () => onAction?.('dismissed');
  const handlePrimary = () => {
    if (announcement.primaryCtaUrl) {
      window.open(announcement.primaryCtaUrl, '_blank', 'noopener,noreferrer');
    }
    onAction?.('cta_clicked');
  };
  const handleSecondary = () => onAction?.('deferred');

  return (
    <Alert
      open={isOpen}
      onClose={dismissible ? handleDismiss : () => {}}
      size="xl"
      dismissible={dismissible}
      zIndex={40}
    >
      {/*
        Title + body support basic HTML (<b>, <em>, <a>) per the
        canonical Strapi content guide. dangerouslySetInnerHTML +
        whitespace-pre-line cooperates: HTML parses normally, and
        literal \n in CMS strings render as line breaks.
        (Future: re-attempt blocks RTE conversion with proper
        pre-sync data-prep.)
      */}
      {announcement.title && (
        <AlertTitle dangerouslySetInnerHTML={{ __html: announcement.title }} />
      )}

      {announcement.body && (
        <AlertDescription
          className="whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: announcement.body }}
        />
      )}

      {(hasVideo || hasImage) && (
        <AlertBody>
          {hasVideo && (
            <div className="relaunch-video-container">
              <div className="plyr-wrapper">
                <Plyr
                  ref={plyrRef}
                  source={{
                    type: 'video',
                    sources: [{ src: announcement.videoUrl, type: 'video/mp4' }],
                  }}
                  options={plyrOptions}
                />
              </div>
            </div>
          )}
          {hasImage && (
            // Render at natural aspect ratio, capped at max-h-64 (256px) and
            // centered. AnnouncementHost preloads the image before flipping
            // the dialog open, so it lands in cache by the time we paint —
            // the dialog opens at its final size without a reserved box.
            // object-contain so we never crop the uploaded asset, regardless
            // of whether CS uploads landscape, square, or portrait artwork.
            // eslint-disable-next-line jsx-a11y/alt-text -- decorative, title carries semantics
            <img
              src={announcement.imageUrl}
              alt={announcement.title || ''}
              className="mx-auto max-h-64 w-auto rounded-lg object-contain"
            />
          )}
        </AlertBody>
      )}

      {(hasPrimary || hasSecondary) && (
        <AlertActions>
          {hasSecondary && (
            <Button variant="plain" onClick={handleSecondary}>
              {announcement.secondaryCtaLabel}
            </Button>
          )}
          {hasPrimary && (
            <Button color="dark" onClick={handlePrimary} autoFocus>
              {announcement.primaryCtaLabel}
            </Button>
          )}
        </AlertActions>
      )}
    </Alert>
  );
};

export default AnnouncementDialog;
